/**
 * Temps local. docs/01 §8: "la part que es trenca sola".
 *
 * Tota consulta de "avui" passa per aquí. És el punt on es decideix si "què he de fer
 * avui" és correcte, i és el punt on les implementacions ingènues fallen dos dies l'any
 * sense donar cap error visible.
 *
 * Cada usuari té el seu `timezone`. Les consultes es resolen en el fus de QUI MIRA, no
 * en el del servidor ni en el de qui va crear la tasca.
 */
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Desplaçament d'un fus respecte a UTC, en minuts, per a un instant concret.
 *
 * Es calcula formatant l'instant en aquell fus i restant. Es fa així i no amb una taula
 * perquè el desplaçament depèn de l'instant: canvia amb l'horari d'estiu, i n'hi ha que
 * no són hores senceres (Pacific/Chatham és +12:45 i +13:45).
 */
function offsetMinutes(timezone, at) {
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = {};
    for (const { type, value } of fmt.formatToParts(at)) {
        if (type !== 'literal')
            parts[type] = Number(value);
    }
    // `hour` pot sortir 24 amb hour12:false a l'hora zero segons l'entorn.
    const hour = parts.hour === 24 ? 0 : (parts.hour ?? 0);
    const asUTC = Date.UTC(parts.year ?? 0, (parts.month ?? 1) - 1, parts.day ?? 1, hour, parts.minute ?? 0, parts.second ?? 0);
    return (asUTC - at.getTime()) / 60_000;
}
/**
 * Converteix una hora de paret local (any, mes, dia, h, min) d'un fus a l'instant UTC.
 *
 * Cal iterar: per saber el desplaçament necessitem l'instant, i per saber l'instant
 * necessitem el desplaçament. Dues passades convergeixen sempre, perquè la segona ja
 * parteix d'un instant del costat correcte del salt.
 *
 * Als salts d'hora hi ha dos casos que no tenen resposta única, i els resolem com fa
 * tothom:
 *   - Hora inexistent (la matinada que se salta a la primavera): cau a l'instant
 *     immediatament posterior al salt.
 *   - Hora ambigua (la matinada repetida a la tardor): s'agafa la primera ocurrència.
 */
function wallClockToUTC(timezone, y, m, d, hh = 0, mm = 0) {
    const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
    let guess = new Date(naive - offsetMinutes(timezone, new Date(naive)) * 60_000);
    guess = new Date(naive - offsetMinutes(timezone, guess) * 60_000);
    return guess;
}
/**
 * Límits UTC del dia local `date` al fus `timezone`.
 *
 * **No es fa `inici + 24 h`.** Els dies de canvi d'hora tenen 23 o 25 hores, i fer-ho
 * amb una suma dona resultats incorrectes dos dies l'any sense cap error visible
 * (docs/01 §8). Es construeix el començament del dia SEGÜENT i es converteix a UTC.
 */
export function localDayBounds(timezone, date) {
    const m = DATE_RE.exec(date);
    if (m === null) {
        throw new Error(`localDayBounds espera una data YYYY-MM-DD, i ha rebut "${date}"`);
    }
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const start = wallClockToUTC(timezone, year, month, day);
    // El dia següent es calcula sobre el calendari, no sumant mil·lisegons.
    const nextUTC = new Date(Date.UTC(year, month - 1, day + 1));
    const next = wallClockToUTC(timezone, nextUTC.getUTCFullYear(), nextUTC.getUTCMonth() + 1, nextUTC.getUTCDate());
    return { startUTC: start.toISOString(), endUTC: next.toISOString() };
}
/** La data local que correspon a un instant, al fus donat. */
export function localDateOf(timezone, at) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    // `en-CA` formata com a YYYY-MM-DD, que és exactament el que guardem.
    return fmt.format(at);
}
/** Quantes hores té un dia local. 23 o 25 als diumenges de canvi d'hora. */
export function localDayLengthHours(timezone, date) {
    const { startUTC, endUTC } = localDayBounds(timezone, date);
    return (Date.parse(endUTC) - Date.parse(startUTC)) / 3_600_000;
}
/**
 * L'instant UTC d'una hora de paret d'un dia local: `('Europe/Madrid', '2026-08-12', '09:00')`.
 *
 * Ho necessita l'horari laboral, que es diu en hores de paret —«de nou a sis»— i s'ha de
 * comparar amb blocs que es guarden en UTC. Reaprofita la conversió que ja fa
 * `localDayBounds`, amb les seves dues passades i el seu criteri per als salts d'hora.
 */
export function localTimeToInstant(timezone, date, time) {
    const d = DATE_RE.exec(date);
    if (d === null) {
        throw new Error(`localTimeToInstant espera una data YYYY-MM-DD, i ha rebut "${date}"`);
    }
    const t = /^(\d{2}):(\d{2})$/.exec(time);
    if (t === null) {
        throw new Error(`localTimeToInstant espera una hora HH:MM, i ha rebut "${time}"`);
    }
    return wallClockToUTC(timezone, Number(d[1]), Number(d[2]), Number(d[3]), Number(t[1]), Number(t[2])).toISOString();
}
/**
 * El dia de la setmana local, **començant en dilluns**: 0 = dilluns … 6 = diumenge.
 *
 * En dilluns i no en diumenge perquè és l'ordre en què s'escriuen els dies laborables
 * (`scope_settings.work_days`, `'1111100'`), i perquè és el primer dia de la setmana als tres
 * idiomes de la casa.
 */
export function localWeekdayOf(timezone, at) {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(at);
    const index = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(name);
    if (index < 0)
        throw new Error(`Dia de la setmana no reconegut: "${name}"`);
    return index;
}
//# sourceMappingURL=timezone.js.map